import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function Header({ onContactClick }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const isTenderPage = location.pathname === "/tender-red-review";

  const navLinks = [
    { href: "/tender-red-review", label: "Tender Red Review", homeOnly: false },
    { href: "/services", label: "Estimating Services", homeOnly: false },
    { href: "/price-desk", label: "Price Desk", homeOnly: false },
    { href: "/about", label: "About", homeOnly: false },
    { href: "/blog", label: "Insights", homeOnly: false },
  ];

  const homeLinks = [
    { href: "#how-it-works", label: "How It Works" },
  ];

  const handleScrollLink = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const handleContactClick = () => {
    if (isTenderPage) {
      // On tender page, scroll to the tender form
      const formSection = document.getElementById("tender-form");
      if (formSection) {
        formSection.scrollIntoView({ behavior: "smooth" });
      }
    } else if (isHomePage) {
      // On home page, scroll to tender form or contact
      const tenderSection = document.getElementById("tender-form");
      if (tenderSection) {
        tenderSection.scrollIntoView({ behavior: "smooth" });
      } else {
        onContactClick();
      }
    } else {
      window.location.href = "/tender-red-review#tender-form";
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex flex-col items-center"
            data-testid="logo"
          >
            <span className="font-heading font-bold text-xl md:text-2xl tracking-tight uppercase whitespace-nowrap">
              BLACKLINE <span className="font-normal">ESTIMATING</span>
            </span>
            <span className="hidden xs:block text-[7px] sm:text-[8px] md:text-[9px] uppercase tracking-[0.08em] text-zinc-600 font-bold mt-0.5 text-center whitespace-nowrap">
              Cost Engineering · Tender Assurance
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {/* Page Links */}
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-body font-medium text-sm uppercase tracking-wider transition-colors ${
                  location.pathname === link.href
                    ? "text-black"
                    : "text-zinc-600 hover:text-black"
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Home scroll links (only on home page) */}
            {isHomePage &&
              homeLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleScrollLink(e, link.href)}
                  className="font-body font-medium text-sm uppercase tracking-wider text-zinc-600 hover:text-black transition-colors"
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}

            <button
              onClick={handleContactClick}
              className="bg-black text-white h-10 px-6 uppercase font-bold text-sm tracking-widest border-2 border-black btn-brutal"
              data-testid="nav-cta"
            >
              Submit Tender
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav
            className="lg:hidden py-4 border-t-2 border-black mobile-menu-enter"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-4">
              {/* Page Links */}
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`font-body font-medium text-sm uppercase tracking-wider py-2 ${
                    location.pathname === link.href
                      ? "text-black"
                      : "text-zinc-600"
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Home scroll links (only on home page) */}
              {isHomePage &&
                homeLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleScrollLink(e, link.href)}
                    className="font-body font-medium text-sm uppercase tracking-wider py-2 text-zinc-600"
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </a>
                ))}

              <button
                onClick={handleContactClick}
                className="bg-black text-white h-12 px-6 uppercase font-bold text-sm tracking-widest border-2 border-black mt-2"
                data-testid="mobile-nav-cta"
              >
                Submit Tender
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
